import styled from '@emotion/styled';
import {Container, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';

import {Slider} from 'UI/components/action/Slider';
import {BlockIntro} from 'UI/components/layout/BlockIntro';
import {PayloadImage} from 'UI/components/layout/PayloadImage';
import {ViewportTrigger} from 'UI/components/layout/ViewportTrigger';

import {FadeInAnimation} from 'UI/animations/shared';

import {isDefinedAndPopulated} from 'Application/utils/typeGuards';

import type {ImageSliderBlockType, ModuleIntro} from 'types/payload-types';


/**
 * The `ComponentProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface ComponentProps {
    /**
     * Determines whether the image slider should automatically advance through slides without user interaction.
     * This boolean property controls the automatic progression behavior of the slider component.
     */
    autoplay: ImageSliderBlockType['autoplay'];
    /**
     * Specifies the time interval in milliseconds between automatic slide transitions when autoplay is enabled.
     * This numeric value determines how long each slide is displayed before automatically advancing to the next one.
     */
    autoplaySpeed: ImageSliderBlockType['autoplaySpeed'];
    /**
     * Unique identifier for the image grid block used for block intro functionality and component identification.
     * This identifier helps with content management and enables proper block-level configuration within the CMS.
     */
    id: string;
    /**
     * Module intro configuration object containing introductory content and settings for the block.
     * This object defines how the block introduction should be displayed above the image grid content.
     */
    intro: ModuleIntro;
    /**
     * Array of content items containing image data, titles, and optional descriptions for grid display.
     * Each item represents a single grid cell with its associated visual and textual content.
     */
    slides: ImageSliderBlockType['slides'];
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'ImageSliderBlock'
     */
    testId?: string;
}

/**
 * The `ImageSliderBlock` component renders an interactive image slider with configurable autoplay functionality and block introduction.
 * This component displays a collection of images in a horizontal slider format, allowing users to navigate through multiple images manually or automatically.
 * It integrates with the Payload CMS system to provide a content-managed image slider block with proper accessibility and testing support.
 *
 * @param props               The component props.
 * @param props.autoplay      Determines whether the image slider should automatically advance through slides without user interaction.
 * @param props.autoplaySpeed Specifies the time interval in milliseconds between automatic slide transitions when autoplay is enabled.
 * @param props.id            Unique identifier for the image slider block used for block intro functionality and component identification.
 * @param props.intro         Module intro configuration object containing introductory content and settings for the block.
 * @param props.slides        Array of content items containing image data, titles, and optional descriptions for slider display.
 * @param props.testId        The unique identifier for testing purposes.
 * @returns The rendered image slider block component with introduction and interactive slider functionality.
 *
 * @example
 * ```tsx
 * <ImageSliderBlock
 *     autoplay={true}
 *     autoplaySpeed={3000}
 *     id="slider-block-1"
 *     intro={{
 *         title: "Featured Images",
 *         description: "Browse our collection"
 *     }}
 *     slides={[
 *         {
 *             id: "slide-1",
 *             image: {
 *                 url: "/image1.jpg",
 *                 alt: "First image"
 *             }
 *         }
 *     ]}
 *     testId="ImageSliderBlock"
 * />
 * ```
 */
const ImageSliderBlock = observer(({
    autoplay,
    autoplaySpeed,
    id,
    intro,
    slides,
    testId = 'ImageSliderBlock'
}: ComponentProps) => (
    <Container testId={testId}>
        <BlockIntro blockId={id} intro={intro} testId={`${testId}-Intro`} />
        <ViewportTrigger baseComponent="div">
            <motion.div variants={FadeInAnimation}>
                <Slider autoplay={autoplay} autoplaySpeed={autoplaySpeed} gap={spacing(22)} testId={`${testId}-Slider`} loop>
                    {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                    {slides?.map(slide => {
                        if (isDefinedAndPopulated(slide)) {
                            return (
                                <ImageWrapper key={slide.id}>
                                    <SliderImage
                                        key={slide.id}
                                        image={slide.image}
                                    />
                                </ImageWrapper>
                            );
                        }

                        return null;
                    })}
                </Slider>
            </motion.div>
        </ViewportTrigger>
    </Container>
));

ImageSliderBlock.displayName = 'ImageSliderBlock';

export {ImageSliderBlock};

const ImageWrapper = styled.div`
    align-items: center;
    display: flex;
    grid-row: span 6;
    justify-content: center;
    width: min-content;

    &:last-of-type {
        margin-inline-end: ${spacing(22)};
    }
`;

const SliderImage = styled(PayloadImage)`
    height: 100%;
    max-height: 7rem;
    max-width: 17.3rem;
    width: auto;
`;